import { useState, useEffect, useCallback, memo, Fragment } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Factory, Plus, Trash, UploadSimple, DownloadSimple, CheckCircle, WarningCircle, CircleNotch, ArrowsClockwise, CaretUpDown, Copy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { InventoryAdjustmentRequests } from "@/components/InventoryAdjustmentRequests";
import { InventoryMovementAuditDashboard, SapValuationBlock } from "@/components/InventoryMovementAuditDashboard";
import { FinancialAuditView } from "@/components/FinancialAuditView";
import { PhysicalInventoryAudit } from "@/components/PhysicalInventoryAudit";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyLine = () => ({ product_id: "", quantity: "", logistics_area_id: "", quantity_uom: "", description: null, available_qty: null, issue: null, uom_confirmed: false });

const STATUS_BADGE = {
  posted: { label: "Posted", className: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  failed: { label: "Failed", className: "bg-[#FEE4E2] text-[#B42318] border-[#FECDCA]" },
  pending: { label: "Posting to SAP...", className: "bg-[#FEF0C7] text-[#93370D] border-[#FEDF89]" },
  // A write timeout is NOT a rejection - SAP may hold the document anyway,
  // so this is amber and must be settled against SAP by hand.
  unknown: { label: "Unknown - verify in SAP", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success(`Copied "${text}"`);
};

// Searchable "Product ID @ Logistics Area" picker backed by live-cached
// stock (Sep 29 2026, user's explicit ask: "show product id dropdown
// selection with the current stock"). One row per (product, area) since
// the same product can sit in more than one warehouse at a site.
// Sep 29 2026 perf fix: wrapped in memo + the (idx, option) => onSelect
// signature so typing anywhere else on the page (Requester/Remarks) no
// longer re-builds this component's 1000+ CommandItem elements on every
// keystroke - that render-time `options.map()` call (even while the
// Popover was visually closed) was the actual cause of the reported lag.
const ProductStockCombobox = memo(({ line, options, onSelect, idx }) => {
  const [open, setOpen] = useState(false);
  const label = line.product_id ? `${line.product_id}${line.logistics_area_id ? ` @ ${line.logistics_area_id}` : ""}` : "Select product...";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 w-full flex items-center justify-between px-2.5 rounded-md border border-[#D0D5DD] bg-white text-xs font-mono text-left"
          data-testid={`cost-center-line-product-${idx}`}
        >
          <span className={line.product_id ? "text-[#101828]" : "text-[#98A2B3]"}>{label}</span>
          <CaretUpDown size={12} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start" data-testid={`cost-center-line-product-popover-${idx}`}>
        <Command>
          <CommandInput placeholder="Search product or description..." data-testid={`cost-center-line-product-search-${idx}`} />
          <CommandList>
            <CommandEmpty>No stock found at this site.</CommandEmpty>
            <CommandGroup>
              {open && options.map((o) => (
                <CommandItem
                  key={`${o.product_id}::${o.logistics_area_id}`}
                  value={`${o.product_id} ${o.description || ""} ${o.logistics_area_id}`}
                  onSelect={() => { onSelect(idx, o); setOpen(false); }}
                  data-testid={`cost-center-line-product-option-${idx}-${o.product_id}-${o.logistics_area_id}`}
                >
                  <div className="flex flex-col py-0.5">
                    <span className="font-mono text-xs font-bold text-[#101828]">{o.product_id} <span className="text-[#667085] font-normal">@ {o.logistics_area_id}</span></span>
                    <span className="text-xs text-[#667085]">{o.description || "—"} · {Number(o.qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })} {o.uom} available</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

// Sep 30 2026, user's ask: a Goods RETURN must be postable for an item
// with NO stock (a return is what creates its stock), so this picker
// searches the material master server-side instead of the stock list -
// see /api/cost-center-consumption/material-search. Used only when
// Movement Type = "return"; Goods Issue keeps the stock-only picker above.
const MaterialSearchCombobox = memo(({ line, idx, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || query.trim().length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/cost-center-consumption/material-search`, { params: { q: query.trim() } });
        setResults(data.materials || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [query, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 w-full flex items-center justify-between px-2.5 rounded-md border border-[#D0D5DD] bg-white text-xs font-mono text-left"
          data-testid={`cost-center-line-material-${idx}`}
        >
          <span className={line.product_id ? "text-[#101828]" : "text-[#98A2B3]"}>{line.product_id || "Search any material..."}</span>
          <CaretUpDown size={12} className="text-[#667085] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start" data-testid={`cost-center-line-material-popover-${idx}`}>
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Product ID (e.g. F62) or description..." data-testid={`cost-center-line-material-search-${idx}`} />
          <CommandList>
            <CommandEmpty>
              {loading ? "Searching SAP..." : query.trim().length < 2 ? "Type at least 2 characters" : "No material found with that ID"}
            </CommandEmpty>
            <CommandGroup>
              {results.map((m) => (
                <CommandItem
                  key={m.product_id}
                  value={m.product_id}
                  onSelect={() => { onSelect(idx, m); setOpen(false); }}
                  data-testid={`cost-center-line-material-option-${idx}-${m.product_id}`}
                >
                  <div className="flex flex-col py-0.5">
                    <span className="font-mono text-xs font-bold text-[#101828]">{m.product_id}</span>
                    <span className="text-xs text-[#667085]">{m.description || "—"}{m.uom ? ` · ${m.uom}` : ""}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});

export default function CostCenterConsumptionPage() {
  const [settings, setSettings] = useState([]);

  const [siteId, setSiteId] = useState("");
  const [movementType, setMovementType] = useState("issue");
  const [requesterName, setRequesterName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [validating, setValidating] = useState(false);
  const [validateElapsed, setValidateElapsed] = useState(0);
  const [validateProgress, setValidateProgress] = useState(null);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [posting, setPosting] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveGacId, setResolveGacId] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const verifyGac = async (logId, gacIdOverride = "") => {
    setVerifying(true);
    try {
      const { data } = await axios.get(`${API}/cost-center-consumption/verify-gac/${logId}`, {
        params: gacIdOverride ? { gac_id: gacIdOverride } : {},
      });
      setVerifyResult(data);
      if (data.found_in_sap && data.matched === data.sent_line_count) toast.success(`All ${data.matched} line(s) confirmed in SAP`);
      else if (!data.found_in_sap) toast.warning(`SAP holds no confirmation ${data.sap_gac_id}`);
      else toast.warning("SAP's document does not fully match - see the details");
    } catch (e) {
      setVerifyResult(null);
      toast.error(e.response?.data?.detail || "Could not read this confirmation from SAP", { duration: 20000 });
    } finally {
      setVerifying(false);
    }
  };
  const [users, setUsers] = useState([]);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [restoredAt, setRestoredAt] = useState(null);

  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [detailEntry, setDetailEntry] = useState(null);
  const [tab, setTab] = useState("submission");
  const [refreshingValuation, setRefreshingValuation] = useState(false);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [flagNote, setFlagNote] = useState("");
  const [pendingAdjustments, setPendingAdjustments] = useState(0);
  const [flagging, setFlagging] = useState(false);

  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [refreshingStock, setRefreshingStock] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [activatingIdx, setActivatingIdx] = useState(null);
  const [costInputs, setCostInputs] = useState({});
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [clearOnly, setClearOnly] = useState(false);

  const isReturn = movementType === "return";

  const loadStock = useCallback(async (site) => {
    if (!site) return;
    setStockLoading(true);
    try {
      const { data } = await axios.get(`${API}/cost-center-consumption/stock`, { params: { site_id: site } });
      setStockOptions(data.stock || []);
    } catch (e) {
      toast.error("Failed to load current stock");
    } finally {
      setStockLoading(false);
    }
  }, []);

  useEffect(() => {
    if (siteId) loadStock(siteId);
  }, [siteId, loadStock]);

  useEffect(() => {
    if (!siteId) return;
    axios.get(`${API}/cost-center-consumption/warehouses`, { params: { site_id: siteId } })
      .then(({ data }) => setWarehouses(data.warehouses || []))
      .catch(() => setWarehouses([]));
  }, [siteId]);

  const selectLineMaterial = useCallback((idx, material) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? {
      ...l, product_id: material.product_id, description: material.description,
      quantity_uom: material.uom || l.quantity_uom, available_qty: null, issue: null,
    } : l)));
    setValidated(false);
  }, []);

  // One-click fix for the two master-data blockers a Goods Return can hit
  // (see cost_center_consumption_service.check_return_master_data) - reuses
  // the same SAP activation + Cost push the STO/GRN flows already use.
  const handleActivateAtSite = async (idx, line) => {
    setActivatingIdx(idx);
    try {
      const amountRaw = costInputs[idx];
      const amount = amountRaw !== undefined && amountRaw !== "" ? Number(amountRaw) : null;
      const { data } = await axios.post(`${API}/admin/material-sites/activate`, {
        product_id: line.product_id, site_id: siteId, ...(amount !== null ? { amount } : {}),
      });
      const failed = Object.entries(data || {}).filter(([, v]) => typeof v === "string" && v !== "ok");
      if (failed.length) toast.error(`SAP: ${failed.map(([k, v]) => `${k}: ${v}`).join(" | ")}`);
      else toast.success(`${line.product_id} activated at ${siteId} - revalidating...`);
      await runValidate(lines);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not activate this material at the site");
    } finally {
      setActivatingIdx(null);
    }
  };

  const handleRefreshStock = async () => {
    if (refreshingStock) return;
    setRefreshingStock(true);
    try {
      const { data } = await axios.post(`${API}/stock-transfer/refresh-site-stock`, null, { params: { site_id: siteId } });
      const jobId = data.job_id;
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await axios.get(`${API}/stock-transfer/refresh-site-stock/${jobId}`);
        if (status.status === "done") { toast.success(`Stock refreshed from SAP (${status.result?.rows_found ?? 0} rows)`); break; }
        if (status.status === "failed") { toast.error(status.error || "Stock refresh failed"); break; }
      }
      await loadStock(siteId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refresh stock");
    } finally {
      setRefreshingStock(false);
    }
  };

  const selectLineStock = useCallback((idx, option) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? {
      ...l, product_id: option.product_id, logistics_area_id: option.logistics_area_id,
      quantity_uom: option.uom, description: option.description, available_qty: option.qty, issue: null,
    } : l)));
    setValidated(false);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cost-center-consumption/settings`);
      setSettings(data.settings || []);
      if (data.settings?.length) setSiteId((prev) => prev || data.settings[0].site_id);
    } catch (e) {
      toast.error("Failed to load Site / Cost Center settings");
    }
  }, [siteId]);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const { data } = await axios.get(`${API}/cost-center-consumption/log`);
      setLog(data.log || []);
    } catch (e) {
      toast.error("Failed to load Audit Log");
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadLog();
    axios.get(`${API}/admin/users`).then(({ data }) => setUsers(data.users || [])).catch(() => {});
    // The pending-adjustment badge has to be right on the FIRST paint, before
    // the Inventory Adjustment Requests tab is ever opened.
    axios.get(`${API}/physical-inventory/requests`, { params: { scope: "all" } })
      .then(({ data }) => setPendingAdjustments(data.pending_count || 0)).catch(() => {});
  }, [loadSettings, loadLog]);

  // Sep 30 2026, user's ask ("start draft save so i can't upload again and
  // again"): the form is autosaved server-side per user, so an 817-line
  // Excel upload survives a failed Validate, a reload or a closed tab.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cost-center-consumption/draft`);
        if (cancelled || !data?.draft?.lines?.length) { setDraftLoaded(true); return; }
        const d = data.draft;
        if (d.site_id) setSiteId(d.site_id);
        if (d.movement_type) setMovementType(d.movement_type);
        setRequesterName(d.requester_name || "");
        setRemarks(d.remarks || "");
        setLines(d.lines);
        setRestoredAt(data.saved_at);
      } catch (e) {
        // no draft is the normal case
      } finally {
        if (!cancelled) setDraftLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasContent = lines.some((l) => l.product_id) || requesterName.trim() || remarks.trim();

  useEffect(() => {
    if (!draftLoaded || !hasContent) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.put(`${API}/cost-center-consumption/draft`, {
          site_id: siteId, movement_type: movementType, requester_name: requesterName, remarks,
          lines: lines.filter((l) => l.product_id),
        });
        setDraftSavedAt(data.saved_at);
      } catch (e) {
        // best-effort - never interrupt data entry
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [draftLoaded, hasContent, siteId, movementType, requesterName, remarks, lines]);

  const discardDraft = async () => {
    await axios.delete(`${API}/cost-center-consumption/draft`).catch(() => {});
    setRestoredAt(null);
    setDraftSavedAt(null);
    resetForm();
    toast.success("Draft discarded");
  };

  const currentCostCenter = settings.find((s) => s.site_id === siteId)?.cost_center_id || "";

  const resetForm = () => {
    setLines([emptyLine()]);
    setRequesterName("");
    setRemarks("");
    setIdempotencyKey(uuidv4());
    setValidated(false);
    setDraftSavedAt(null);
    setRestoredAt(null);
    axios.delete(`${API}/cost-center-consumption/draft`).catch(() => {});
  };

  const updateLine = (idx, field, value) => {
    // Changing a line's unit withdraws any earlier "Keep <unit>" consent.
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value, issue: null, ...(field === "quantity_uom" ? { uom_confirmed: false } : {}) } : l)));
    setValidated(false);
  };
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Sep 30 2026, user's ask after an 816-line upload flagged 23 lines: most
  // were a 2-decimal Excel asking 0.75 of the 0.745 SAP actually holds, and
  // hunting them down meant scrolling 816 rows. These three bulk actions
  // (plus the issues-only filter) act on the structured numbers the backend
  // now sends with every insufficient_stock issue.
  const shortLines = lines.filter((l) => l.issue?.kind === "insufficient_stock");
  const uomMismatchLines = lines.filter((l) => ["uom_no_conversion", "uom_conversion_warning"].includes(l.issue?.kind));
  const roundingLines = shortLines.filter((l) => l.issue.rounding_only);
  const genuinelyShortLines = shortLines.filter((l) => !l.issue.rounding_only);
  const issueCount = lines.filter((l) => l.issue).length;
  const clearLineCount = lines.length - issueCount;

  const applyAvailableQty = async (predicate, label) => {
    const next = lines.map((l) => (predicate(l) ? { ...l, quantity: l.issue.available_qty, issue: null } : l));
    setLines(next);
    toast.success(label);
    await runValidate(next);
  };

  const fixUomMismatches = async () => {
    const next = lines.map((l) => (["uom_no_conversion", "uom_conversion_warning"].includes(l.issue?.kind)
      ? { ...l, quantity_uom: l.issue.stock_uom, uom_confirmed: false, issue: null } : l));
    setLines(next);
    toast.success(`${uomMismatchLines.length} line(s) switched to the unit SAP holds the stock in`);
    await runValidate(next);
  };

  const downloadIssuesExcel = async () => {
    try {
      const res = await axios.post(`${API}/cost-center-consumption/issues-export`, {
        site_id: siteId, movement_type: movementType, lines: lines.filter((l) => l.issue),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cost_center_issues_${siteId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${issueCount} flagged line(s) downloaded`);
    } catch (e) {
      toast.error("Could not build the issues Excel");
    }
  };

  const removeShortLines = async () => {
    const next = lines.filter((l) => !(l.issue?.kind === "insufficient_stock" && !l.issue.rounding_only));
    const removed = lines.length - next.length;
    setLines(next.length ? next : [emptyLine()]);
    toast.success(`${removed} short line(s) removed - ${next.length} line(s) left`);
    if (next.length) await runValidate(next);
  };

  // Oct 1 2026: validation runs server-side as a polled job - a slow SAP
  // used to blow past the ingress timeout and surface as a Cloudflare
  // "origin returned an invalid or incomplete response" error.
  const runValidate = async (linesToCheck) => {
    setValidating(true);
    setValidateElapsed(0);
    setValidateProgress(null);
    let updatedLines = linesToCheck;
    const ticker = setInterval(() => setValidateElapsed((s) => s + 1), 1000);
    try {
      const payload = {
        site_id: siteId, movement_type: movementType,
        lines: linesToCheck.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0, logistics_area_id: l.logistics_area_id, quantity_uom: l.quantity_uom, uom_confirmed: !!l.uom_confirmed })),
      };
      const { data: job } = await axios.post(`${API}/cost-center-consumption/validate-job`, payload);
      let data = null;
      for (let i = 0; i < 450 && !data; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await axios.get(`${API}/cost-center-consumption/validate-job/${job.job_id}`);
        if (status.status === "done") data = status.result;
        else if (status.status === "failed") throw new Error(status.error || "Validation failed");
        else if (status.to_check) setValidateProgress({ checked: status.checked || 0, total: status.to_check });
      }
      if (!data) throw new Error("SAP is taking unusually long to answer - please try Validate again in a few minutes.");
      const checkedLines = data.lines || [];
      updatedLines = linesToCheck.map((l, i) => {
        const c = checkedLines[i];
        if (!c) return l;
        return { ...l, description: c.description, quantity_uom: l.quantity_uom || c.uom, issue: (c.issues || [])[0] || null };
      });
      setLines(updatedLines);
      setValidated(true);
      if (data.ok) toast.success("All lines validated - stock and product checks passed");
      else {
        const issues = updatedLines.filter((l) => l.issue);
        const rounding = issues.filter((l) => l.issue.rounding_only).length;
        toast.error(`${issues.length} of ${updatedLines.length} line(s) need attention${rounding ? ` - ${rounding} are only rounding differences` : ""}`);
      }
      return { ok: data.ok, lines: updatedLines };
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Validation failed", { duration: 15000 });
      return { ok: false, lines: updatedLines };
    } finally {
      clearInterval(ticker);
      setValidating(false);
      setValidateElapsed(0);
      setValidateProgress(null);
    }
  };

  const handleValidate = () => runValidate(lines);

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/cost-center-consumption/bulk-upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) {
        data.errors.forEach((err) => toast.error(err));
      }
      if (data.lines?.length) {
        const newLines = data.lines.map((l) => ({ ...emptyLine(), ...l }));
        setValidated(false);
        toast.success(`${data.lines.length} line(s) loaded from Excel - resolving descriptions & units...`);
        runValidate(newLines);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse the uploaded file");
    }
  };

  // Sep 30 2026: a big document (the live case was 817 lines) takes SAP
  // minutes, so /submit returns a "pending" entry and the real outcome is
  // polled from the Audit Log. The posting itself runs server-side, so
  // closing the tab no longer loses it.
  const handleSubmit = async () => {
    if (!requesterName.trim()) { toast.error("Requester Name is required"); return; }
    // Sep 30 2026, user's ask ("clear ok item so ok item can be
    // successfully goods issue in the sap"): with "Clear (OK) items only"
    // ticked, ONLY the unflagged lines go to SAP and the flagged ones stay
    // behind in the form/draft to be corrected - SAP posts the document
    // atomically, so one bad line would otherwise block all 800 good ones.
    const heldBack = clearOnly ? lines.filter((l) => l.issue) : [];
    const { ok, lines: validatedLines } = await runValidate(lines);
    // With "Clear (OK) items only" ticked we deliberately post DESPITE the
    // flagged lines - they're excluded from this document, not fixed. Note
    // every line is still validated (state stays intact); only the posting
    // payload is narrowed.
    const toPost = clearOnly ? validatedLines.filter((l) => !l.issue) : validatedLines;
    const held = clearOnly ? validatedLines.filter((l) => l.issue) : heldBack;
    if (!ok && !clearOnly) return;
    if (!toPost.length) { toast.error("No clear lines to post"); return; }
    setSubmitting(true);
    setPosting(null);
    try {
      const { data } = await axios.post(`${API}/cost-center-consumption/submit`, {
        site_id: siteId, movement_type: movementType, requester_name: requesterName, remarks,
        lines: toPost.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) || 0, logistics_area_id: l.logistics_area_id, quantity_uom: l.quantity_uom, uom_confirmed: !!l.uom_confirmed })),
        idempotency_key: idempotencyKey,
      });
      if (data.status === "pending") {
        setPosting({ id: data.id, lineCount: data.lines?.length || toPost.length, startedAt: Date.now() });
        await pollPosting(data.id, held);
      } else if (data.status === "posted") {
        toast.success(`Posted to SAP successfully - GAC ID ${data.sap_gac_id}`);
        finishPosted(held);
      } else {
        toast.error(data.error || "SAP rejected this posting");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const finishPosted = (heldBack) => {
    if (heldBack.length) {
      setLines(heldBack);
      setIdempotencyKey(uuidv4());
      setValidated(false);
      setClearOnly(false);
      toast.info(`${heldBack.length} flagged line(s) kept here to correct and post separately`, { duration: 12000 });
    } else {
      resetForm();
    }
    loadLog();
  };

  const pollPosting = async (logId, heldBack = []) => {
    for (let attempt = 0; attempt < 260; attempt += 1) {
      await new Promise((r) => setTimeout(r, 4000));
      let entry;
      try {
        ({ data: entry } = await axios.get(`${API}/cost-center-consumption/log/${logId}`));
      } catch (e) {
        continue;
      }
      if (entry.status === "pending") continue;
      setPosting(null);
      if (entry.status === "posted") {
        toast.success(`Posted to SAP successfully - GAC ID ${entry.sap_gac_id}`);
        finishPosted(heldBack);
      } else if (entry.status === "unknown") {
        loadLog();
        toast.warning("SAP did not answer in time - check SAP, then confirm the outcome in the Audit Log", { duration: 15000 });
      } else {
        loadLog();
        toast.error(entry.error || "SAP rejected this posting", { duration: 15000 });
      }
      return;
    }
    setPosting(null);
    toast.warning("Still posting after a long wait - check the Audit Log for the outcome", { duration: 15000 });
  };

  const handleResolveUnknown = async (wasPosted) => {
    if (!detailEntry) return;
    setResolving(true);
    try {
      const { data } = await axios.put(`${API}/cost-center-consumption/log/${detailEntry.id}/resolve-unknown`, { posted: wasPosted, sap_gac_id: resolveGacId });
      // The PUT response carries no SAP valuation - keep the one already loaded.
      setDetailEntry((prev) => ({ ...data, valuation: data.valuation ?? prev?.valuation }));
      setLog((prev) => prev.map((e) => (e.id === data.id ? data : e)));
      setAuditRefreshKey((k) => k + 1);
      toast.success(wasPosted ? "Marked as posted in SAP" : "Marked as not posted - you can re-enter these lines");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update this submission");
    } finally {
      setResolving(false);
    }
  };

  const openDetail = async (entry) => {
    setDetailEntry(entry);
    setFlagNote(entry.correction_note || "");
    setVerifyResult(null);
    // The dashboard row carries only the header - pull the lines and the SAP
    // financial detail for the modal.
    try {
      const { data } = await axios.get(`${API}/cost-center-consumption/log/${entry.id}`);
      setDetailEntry(data);
      setFlagNote(data.correction_note || "");
    } catch (e) {
      // keep whatever the row already gave us
    }
  };

  const refreshValuation = async () => {
    if (!detailEntry?.sap_gac_id) return;
    setRefreshingValuation(true);
    try {
      const { data } = await axios.post(`${API}/cost-center-consumption/log/${detailEntry.id}/refresh-valuation`);
      setDetailEntry((prev) => ({ ...prev, valuation: data.valuation }));
      setAuditRefreshKey((k) => k + 1);
      toast.success(data.valuation?.status === "retrieved"
        ? `SAP value ₹${Number(data.valuation.total_amount_abs).toLocaleString("en-IN", { minimumFractionDigits: 2 })} ${data.valuation.currency}`
        : "SAP released no value for this movement");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the value from SAP");
    } finally {
      setRefreshingValuation(false);
    }
  };

  const handleFlagToggle = async (markAsCorrection) => {
    if (!detailEntry) return;
    setFlagging(true);
    try {
      const { data } = await axios.put(`${API}/cost-center-consumption/log/${detailEntry.id}/flag`, { needs_correction: markAsCorrection, note: flagNote });
      setDetailEntry((prev) => ({ ...data, valuation: data.valuation ?? prev?.valuation }));
      setLog((prev) => prev.map((e) => (e.id === data.id ? data : e)));
      setAuditRefreshKey((k) => k + 1);
      toast.success(markAsCorrection ? "Flagged as needing correction" : "Flag cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update flag");
    } finally {
      setFlagging(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="cost-center-consumption-page">
      {/* App.js already mounts the global <Toaster/> - a second one here showed
          every toast twice. */}
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Factory size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Cost Center Consumption</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className={`p-4 sm:p-6 w-full mx-auto ${tab === "log" ? "max-w-[1440px]" : "max-w-5xl"}`}>
        <div className="flex items-center gap-2 mb-4">
          <Factory size={20} weight="fill" className="text-[#0E7C86]" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Internal Logistics Consumption for Cost Center</h1>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="flex-wrap h-auto" data-testid="cost-center-tabs">
            <TabsTrigger value="submission" data-testid="tab-cost-center-submission">New Submission</TabsTrigger>
            {/* Oct 21 2026, user's spec: physical inventory counted on the
                floor arrives here as an adjustment request - the admin
                reviews the differences and confirms them in one click. */}
            <TabsTrigger value="inventory-requests" data-testid="tab-cost-center-inventory-requests">
              Inventory Adjustment Requests
              {pendingAdjustments > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FEF0C7] text-[#93370D] text-[10px] font-bold" data-testid="inv-adj-pending-count">
                  {pendingAdjustments}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pi-audit" data-testid="tab-physical-inventory-audit">Physical Inventory Audit</TabsTrigger>
            <TabsTrigger value="log" data-testid="tab-cost-center-log">Audit Log</TabsTrigger>
            {/* Oct 28 2026, user's ask: the real money behind the consumed-item
                confirmations, read from their own SAP journal report. */}
            <TabsTrigger value="financial" data-testid="tab-financial-audit">Financial Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="financial" className="space-y-4">
            <FinancialAuditView />
          </TabsContent>

          <TabsContent value="pi-audit" className="space-y-4">
            <PhysicalInventoryAudit />
          </TabsContent>

          <TabsContent value="inventory-requests" className="space-y-4">
            <InventoryAdjustmentRequests onPendingCount={setPendingAdjustments} />
          </TabsContent>

          <TabsContent value="submission" className="space-y-5">
            {restoredAt && (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-[#B2DDFF] bg-[#EFF8FF] p-3" data-testid="cost-center-draft-restored-banner">
                <p className="text-xs text-[#175CD3]">
                  <strong>Draft restored</strong> - {lines.filter((l) => l.product_id).length} line(s) saved on{" "}
                  {new Date(restoredAt).toLocaleString()}. Your entries are saved automatically, so an upload is never lost.
                </p>
                <Button size="sm" variant="outline" onClick={discardDraft} className="h-7 text-xs shrink-0" data-testid="cost-center-discard-draft-btn">
                  Discard Draft
                </Button>
              </div>
            )}
            <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 sm:p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Site</Label>
                  <Select value={siteId} onValueChange={setSiteId}>
                    <SelectTrigger className="bg-white" data-testid="cost-center-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent>
                      {settings.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.site_id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Cost Center</Label>
                  <Input value={currentCostCenter} disabled className="bg-[#F9FAFB] font-mono" data-testid="cost-center-derived-id" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Transaction Type</Label>
                  <Select value={movementType} onValueChange={(v) => { setMovementType(v); setValidated(false); }}>
                    <SelectTrigger className="bg-white" data-testid="cost-center-movement-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="issue">Consumption (Goods Issue)</SelectItem>
                      <SelectItem value="return">Return from Cost Center</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Requester Name</Label>
                  <Input list="cost-center-requester-list" value={requesterName} onChange={(e) => setRequesterName(e.target.value)} placeholder="Type or pick a name" data-testid="cost-center-requester-input" />
                  <datalist id="cost-center-requester-list">
                    {users.map((u) => <option key={u._id} value={u.name || u.email} />)}
                  </datalist>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054] mb-1.5 block">Remarks (posted to SAP as Internal Comment)</Label>
                  <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={90} placeholder="e.g. Consumed for tooling repair" data-testid="cost-center-remarks-input" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold text-[#344054]">Line Items</Label>
                <div className="flex items-center gap-3">
                  {!isReturn && (
                    <Button variant="ghost" size="sm" onClick={handleRefreshStock} disabled={refreshingStock || !siteId} className="h-7 gap-1.5 text-xs text-[#0E7C86]" data-testid="cost-center-refresh-stock-btn">
                      {refreshingStock ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />} Refresh Current Stock
                    </Button>
                  )}
                  <label className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0E7C86] cursor-pointer" data-testid="cost-center-bulk-upload-label">
                    <UploadSimple size={14} weight="bold" /> Bulk Upload (Excel)
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} data-testid="cost-center-bulk-upload-input" />
                  </label>
                  <a href="/templates/cost_center_consumption_template.xlsx" download className="text-xs text-[#667085] underline hover:text-[#0E7C86]" data-testid="cost-center-download-template-link">
                    Download Template
                  </a>
                </div>
              </div>
              {stockLoading && !isReturn && <p className="text-xs text-[#667085]" data-testid="cost-center-stock-loading">Loading current stock for {siteId}...</p>}
              {isReturn && (
                <p className="text-xs text-[#667085]" data-testid="cost-center-return-hint">
                  Returns can use any material in SAP, including items with no stock yet - search by Product ID. Each line is checked for
                  Logistics / Supply Planning / Valuation activation and a non-zero Cost at {siteId || "the selected site"} before posting.
                </p>
              )}

              {issueCount > 0 && (
                <div className="rounded-lg border border-[#FEC84B] bg-[#FFFAEB] p-3 space-y-2" data-testid="cost-center-issue-summary">
                  <p className="text-xs text-[#93370D]">
                    <strong>{issueCount} of {lines.length} line(s) need attention</strong>
                    {roundingLines.length > 0 && ` · ${roundingLines.length} are only rounding differences (your quantity is a whisker above what SAP holds)`}
                    {genuinelyShortLines.length > 0 && ` · ${genuinelyShortLines.length} are genuinely short of stock`}
                    {uomMismatchLines.length > 0 && ` · ${uomMismatchLines.length} use a different unit than SAP's stock`}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {uomMismatchLines.length > 0 && (
                      <Button size="sm" variant="outline" disabled={validating} onClick={fixUomMismatches} className="h-7 text-xs" data-testid="cost-center-fix-uom-btn">
                        Fix unit mismatches ({uomMismatchLines.length})
                      </Button>
                    )}
                    {roundingLines.length > 0 && (
                      <Button size="sm" variant="outline" disabled={validating} onClick={() => applyAvailableQty((l) => l.issue?.rounding_only, `${roundingLines.length} rounding difference(s) set to SAP's exact quantity`)} className="h-7 text-xs" data-testid="cost-center-fix-rounding-btn">
                        Fix rounding differences ({roundingLines.length})
                      </Button>
                    )}
                    {genuinelyShortLines.some((l) => l.issue.available_qty > 0) && (
                      <Button size="sm" variant="outline" disabled={validating} onClick={() => applyAvailableQty((l) => l.issue?.kind === "insufficient_stock" && l.issue.available_qty > 0, "Short lines set to the available quantity")} className="h-7 text-xs" data-testid="cost-center-use-available-btn">
                        Use available quantity ({genuinelyShortLines.filter((l) => l.issue.available_qty > 0).length})
                      </Button>
                    )}
                    {genuinelyShortLines.length > 0 && (
                      <Button size="sm" variant="outline" disabled={validating} onClick={removeShortLines} className="h-7 text-xs text-[#B42318] border-[#FECDCA]" data-testid="cost-center-remove-short-btn">
                        Remove short lines ({genuinelyShortLines.length})
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={validating} onClick={downloadIssuesExcel} className="h-7 text-xs gap-1.5" data-testid="cost-center-download-issues-btn">
                      <DownloadSimple size={13} weight="bold" /> Download issue lines ({issueCount})
                    </Button>
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#93370D] cursor-pointer ml-auto">
                      <input type="checkbox" checked={issuesOnly} onChange={(e) => { setIssuesOnly(e.target.checked); if (e.target.checked) setClearOnly(false); }} data-testid="cost-center-issues-only-toggle" />
                      Show only lines with issues
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-xs font-bold text-[#027A48] cursor-pointer" data-testid="cost-center-clear-only-label">
                      <input type="checkbox" checked={clearOnly} onChange={(e) => { setClearOnly(e.target.checked); if (e.target.checked) setIssuesOnly(false); }} data-testid="cost-center-clear-only-toggle" />
                      Clear (OK) items only - post these {clearLineCount} to SAP
                    </label>
                  </div>
                </div>
              )}

              <div className="border border-[#E4E7EC] rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="text-xs">Product ID</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs">Logistics Area</TableHead>
                      <TableHead className="text-xs">Quantity</TableHead>
                      <TableHead className="text-xs">UOM</TableHead>
                      <TableHead className="text-xs w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((l, idx) => [l, idx]).filter(([l]) => (issuesOnly ? l.issue : clearOnly ? !l.issue : true)).map(([l, idx]) => (
                      <Fragment key={idx}>
                        <TableRow data-testid={`cost-center-line-row-${idx}`}>
                          <TableCell>
                            {isReturn
                              ? <MaterialSearchCombobox idx={idx} line={l} onSelect={selectLineMaterial} />
                              : <ProductStockCombobox idx={idx} line={l} options={stockOptions} onSelect={selectLineStock} />}
                          </TableCell>
                          <TableCell className="text-xs text-[#667085]">{l.description || "—"}</TableCell>
                          <TableCell className="text-xs font-mono text-[#667085]">
                            {isReturn ? (
                              <Select value={l.logistics_area_id || ""} onValueChange={(v) => updateLine(idx, "logistics_area_id", v)}>
                                <SelectTrigger className="h-8 w-[150px] bg-white text-xs font-mono" data-testid={`cost-center-line-area-select-${idx}`}>
                                  <SelectValue placeholder="Select area" />
                                </SelectTrigger>
                                <SelectContent>
                                  {warehouses.map((w) => (
                                    <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`cost-center-line-area-option-${idx}-${w.warehouse_id}`}>
                                      {w.warehouse_id}{w.warehouse_name ? ` · ${w.warehouse_name}` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (l.logistics_area_id || "—")}
                          </TableCell>
                          <TableCell>
                            <Input type="number" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} className="h-8 text-xs w-24" data-testid={`cost-center-line-qty-${idx}`} />
                            {l.available_qty != null && <span className="text-[10px] text-[#667085] block mt-0.5" data-testid={`cost-center-line-available-${idx}`}>Avail: {Number(l.available_qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span>}
                          </TableCell>
                          <TableCell>
                            <Input value={l.quantity_uom} onChange={(e) => updateLine(idx, "quantity_uom", e.target.value.toUpperCase())} placeholder="e.g. EA" className="h-8 text-xs w-16 font-mono" data-testid={`cost-center-line-uom-${idx}`} />
                          </TableCell>
                          <TableCell>
                            <button onClick={() => removeLine(idx)} className="text-[#B42318] disabled:opacity-30" disabled={lines.length === 1} data-testid={`cost-center-line-remove-${idx}`}>
                              <Trash size={14} weight="bold" />
                            </button>
                          </TableCell>
                        </TableRow>
                        {l.issue && (
                          <TableRow>
                            <TableCell colSpan={6} className="py-1.5 text-xs text-[#B42318] bg-[#FEF3F2]" data-testid={`cost-center-line-issue-${idx}`}>
                              <WarningCircle size={12} weight="fill" className="inline mr-1" />{l.issue.message}
                              {l.issue.kind === "uom_no_conversion" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={validating}
                                  onClick={() => updateLine(idx, "quantity_uom", l.issue.stock_uom)}
                                  className="h-6 text-xs ml-2 align-middle"
                                  data-testid={`cost-center-line-use-uom-${idx}`}
                                >
                                  Use {l.issue.stock_uom}
                                </Button>
                              )}
                              {l.issue.kind === "uom_conversion_warning" && (
                                <span className="inline-flex items-center gap-1.5 ml-2 align-middle">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={validating}
                                    onClick={() => updateLine(idx, "quantity_uom", l.issue.stock_uom)}
                                    className="h-6 text-xs"
                                    data-testid={`cost-center-line-use-uom-${idx}`}
                                  >
                                    Use {l.issue.stock_uom}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={validating}
                                    onClick={() => { setLines((prev) => prev.map((x, i) => (i === idx ? { ...x, uom_confirmed: true, issue: null } : x))); setValidated(false); }}
                                    className="h-6 text-xs"
                                    data-testid={`cost-center-line-keep-uom-${idx}`}
                                  >
                                    Keep {l.issue.line_uom} ({Number(l.issue.converted_qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })} {l.issue.stock_uom})
                                  </Button>
                                </span>
                              )}
                              {l.issue.kind === "insufficient_stock" && l.issue.available_qty > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={validating}
                                  onClick={() => updateLine(idx, "quantity", l.issue.available_qty)}
                                  className="h-6 text-xs ml-2 align-middle"
                                  data-testid={`cost-center-line-use-available-${idx}`}
                                >
                                  Use {Number(l.issue.available_qty).toLocaleString("en-IN", { maximumFractionDigits: 5 })}
                                </Button>
                              )}
                              {["not_activated", "zero_valuation"].includes(l.issue.kind) && (
                                <span className="inline-flex items-center gap-1.5 ml-2 align-middle">
                                  {l.issue.kind === "zero_valuation" && (
                                    <Input
                                      type="number"
                                      value={costInputs[idx] ?? ""}
                                      onChange={(e) => setCostInputs((prev) => ({ ...prev, [idx]: e.target.value }))}
                                      placeholder="Cost per unit"
                                      className="h-7 w-28 text-xs"
                                      data-testid={`cost-center-line-cost-input-${idx}`}
                                    />
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={activatingIdx === idx || (l.issue.kind === "zero_valuation" && !costInputs[idx])}
                                    onClick={() => handleActivateAtSite(idx, l)}
                                    className="h-7 text-xs gap-1.5"
                                    data-testid={`cost-center-line-activate-btn-${idx}`}
                                  >
                                    {activatingIdx === idx ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={12} weight="bold" />}
                                    {l.issue.kind === "zero_valuation" ? `Set Cost at ${siteId}` : `Activate at ${siteId}`}
                                  </Button>
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5" data-testid="cost-center-add-line-btn">
                <Plus size={14} weight="bold" /> Add Line
              </Button>

              <div className="flex items-center gap-3 pt-2 border-t border-[#EAECF0]">
                <Button variant="outline" onClick={handleValidate} disabled={validating || !siteId || !!posting} className="gap-1.5" data-testid="cost-center-validate-btn">
                  {validating ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />} Validate
                  {validating && (validateProgress
                    ? <span className="text-xs font-normal" data-testid="cost-center-validate-progress">{validateProgress.checked}/{validateProgress.total} in SAP · {validateElapsed}s</span>
                    : validateElapsed > 3 && <span className="text-xs font-normal">{validateElapsed}s</span>)}
                </Button>
                <Button onClick={handleSubmit} disabled={submitting || !siteId || !!posting} className="bg-[#0E7C86] hover:bg-[#0B6B74] gap-1.5" data-testid="cost-center-submit-btn">
                  {submitting ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
                  {movementType === "issue" ? "Post Consumption" : "Post Return"}
                  {clearOnly && issueCount > 0 ? ` (${clearLineCount} clear line${clearLineCount === 1 ? "" : "s"})` : ""}
                </Button>
                {validated && !posting && <span className="text-xs text-[#667085]">Re-validates automatically before posting.</span>}
                {draftSavedAt && !posting && (
                  <span className="text-xs text-[#667085] ml-auto" data-testid="cost-center-draft-saved-indicator">
                    Draft saved {new Date(draftSavedAt).toLocaleTimeString()}
                  </span>
                )}
              </div>

              {posting && (
                <div className="flex items-start gap-3 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] p-3" data-testid="cost-center-posting-progress">
                  <CircleNotch size={18} className="animate-spin text-[#B54708] mt-0.5 shrink-0" />
                  <div className="text-xs text-[#93370D] space-y-1">
                    <p className="font-bold">Posting {posting.lineCount} line{posting.lineCount === 1 ? "" : "s"} to SAP - this can take a few minutes.</p>
                    <p>
                      SAP processes the whole document as one transaction, so large postings are slow. This keeps running on the server -
                      you can close this tab and check the outcome in the <strong>Audit Log</strong>.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="log" className="space-y-4">
            <InventoryMovementAuditDashboard onOpenDetail={openDetail} refreshKey={auditRefreshKey} />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!detailEntry} onOpenChange={(open) => !open && setDetailEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cost-center-log-detail-dialog">
          <DialogHeader>
            <DialogTitle>Submission Detail</DialogTitle>
            <DialogDescription>Site, Cost Center, Requester, status and line items for this Cost Center posting.</DialogDescription>
          </DialogHeader>
          {detailEntry && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#667085]">Site:</span> <span className="font-mono">{detailEntry.site_id}</span></div>
                <div><span className="text-[#667085]">Cost Center:</span> <span className="font-mono">{detailEntry.cost_center_id}</span></div>
                <div><span className="text-[#667085]">Requester:</span> {detailEntry.requester_name}</div>
                <div><span className="text-[#667085]">Status:</span> <Badge className={STATUS_BADGE[detailEntry.status]?.className}>{STATUS_BADGE[detailEntry.status]?.label}</Badge></div>
                {detailEntry.sap_gac_id && (
                  <div className="col-span-2">
                    <span className="text-[#667085]">SAP GAC ID:</span>{" "}
                    <button
                      onClick={() => copyToClipboard(detailEntry.sap_gac_id)}
                      className="inline-flex items-center gap-1 font-mono font-bold text-[#101828] hover:text-[#0E7C86]"
                      data-testid="cost-center-detail-copy-gac"
                    >
                      {detailEntry.sap_gac_id} <Copy size={12} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
              {detailEntry.reference_id && (
                <div className="text-xs">
                  <span className="text-[#667085]">Reference in SAP:</span>{" "}
                  <button
                    onClick={() => copyToClipboard(detailEntry.reference_id)}
                    className="inline-flex items-center gap-1 font-mono font-bold text-[#101828] hover:text-[#0E7C86]"
                    data-testid="cost-center-detail-copy-reference"
                  >
                    {detailEntry.reference_id} <Copy size={12} weight="bold" />
                  </button>
                  {detailEntry.sap_remark && (
                    <span className="block mt-1 text-[#667085]">Internal Comment sent to SAP: &ldquo;{detailEntry.sap_remark}&rdquo;</span>
                  )}
                </div>
              )}
              {detailEntry.remarks && <div className="text-xs"><span className="text-[#667085]">Remarks:</span> {detailEntry.remarks}</div>}
              {detailEntry.error && <div className="text-xs text-[#B42318]">{detailEntry.error}</div>}

              {detailEntry.status === "posted" && (
                <SapValuationBlock entry={detailEntry} onRefresh={refreshValuation} refreshing={refreshingValuation} />
              )}

              {(detailEntry.sap_gac_id || detailEntry.status === "unknown") && (
                <div className="space-y-2" data-testid="cost-center-verify-block">
                  {detailEntry.sap_gac_id && (
                    <Button size="sm" variant="outline" disabled={verifying} onClick={() => verifyGac(detailEntry.id)} className="h-7 text-xs gap-1.5" data-testid="cost-center-verify-gac-btn">
                      {verifying ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
                      Verify GAC {detailEntry.sap_gac_id} in SAP
                    </Button>
                  )}
                  {verifyResult?.log_id === detailEntry.id && (
                    <div className={`rounded-lg border p-3 text-xs ${verifyResult.matched === verifyResult.sent_line_count ? "border-[#ABEFC6] bg-[#ECFDF3] text-[#027A48]" : "border-[#FEC84B] bg-[#FFFAEB] text-[#93370D]"}`} data-testid="cost-center-verify-result">
                      {!verifyResult.found_in_sap ? (
                        <p><strong>SAP holds no confirmation {verifyResult.sap_gac_id}.</strong> Nothing was posted under this ID.</p>
                      ) : (
                        <p>
                          <strong>{verifyResult.matched} of {verifyResult.sent_line_count} line(s) confirmed in SAP</strong>
                          {verifyResult.missing_in_sap > 0 && ` · ${verifyResult.missing_in_sap} not found in the SAP document`}
                          {verifyResult.quantity_differs > 0 && ` · ${verifyResult.quantity_differs} posted with a different quantity`}
                          {verifyResult.extra_in_sap?.length > 0 && ` · ${verifyResult.extra_in_sap.length} extra line(s) in SAP we never sent`}
                          {verifyResult.cancelled_in_sap > 0 && ` · ${verifyResult.cancelled_in_sap} cancelled item(s) in SAP`}
                          .
                          {verifyResult.posted_at && (
                            <span className="block mt-1 font-normal">
                              SAP posted {String(verifyResult.posted_at).replace("T", " ").replace("Z", "")} · {verifyResult.sap_item_count} item(s)
                              {verifyResult.remark_in_sap ? ` · "${verifyResult.remark_in_sap}"` : ""}
                            </span>
                          )}
                        </p>
                      )}
                      {verifyResult.lines?.filter((l) => l.state !== "matched").slice(0, 25).map((l, i) => (
                        <p key={i} className="mt-1 font-mono" data-testid={`cost-center-verify-diff-${i}`}>
                          {l.product_id} @ {l.logistics_area_id}: sent {l.quantity}
                          {l.state === "missing_in_sap" ? " · not in SAP" : ` · SAP has ${l.sap_quantity}`}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {detailEntry.status === "unknown" && (
                <div className="rounded-lg border border-[#FEDF89] bg-[#FFFAEB] p-3 space-y-2" data-testid="cost-center-resolve-unknown-panel">
                  <p className="text-xs text-[#93370D]">
                    SAP never confirmed this posting, so it may or may not exist there. Open SAP, look for a Goods &amp; Activity Confirmation
                    at <strong>{detailEntry.site_id}</strong> around {(detailEntry.created_at || "").replace("T", " ").slice(0, 16)}, then tell the app what you found.
                    New postings for {detailEntry.site_id} stay blocked until this is settled.
                  </p>
                  <Input value={resolveGacId} onChange={(e) => setResolveGacId(e.target.value)} placeholder="GAC ID from SAP (optional)" className="h-8 text-xs" data-testid="cost-center-resolve-gac-input" />
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={verifying || !resolveGacId.trim()} onClick={() => verifyGac(detailEntry.id, resolveGacId.trim())} className="h-7 text-xs gap-1.5" data-testid="cost-center-check-gac-in-sap-btn">
                      {verifying ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} weight="bold" />}
                      Check this GAC in SAP
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" disabled={resolving} onClick={() => handleResolveUnknown(true)} className="h-7 text-xs bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="cost-center-resolve-posted-btn">
                      {resolving ? <CircleNotch size={12} className="animate-spin" /> : "SAP did post it"}
                    </Button>
                    <Button size="sm" variant="outline" disabled={resolving} onClick={() => handleResolveUnknown(false)} className="h-7 text-xs" data-testid="cost-center-resolve-not-posted-btn">
                      SAP did not post it
                    </Button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader><TableRow><TableHead className="text-xs">Product</TableHead><TableHead className="text-xs">Area</TableHead><TableHead className="text-xs">Qty</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(detailEntry.lines || []).map((l, i) => (
                    <TableRow key={i}><TableCell className="text-xs font-mono">{l.product_id}</TableCell><TableCell className="text-xs font-mono">{l.logistics_area_id}</TableCell><TableCell className="text-xs">{l.quantity} {l.quantity_uom}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t border-[#EAECF0] pt-3 space-y-2">
                {detailEntry.needs_correction && (
                  <p className="text-xs text-[#B42318] bg-[#FEF3F2] rounded-md p-2" data-testid="cost-center-flag-note-display">
                    <WarningCircle size={12} weight="fill" className="inline mr-1" />{detailEntry.correction_note || "Flagged as needing correction."}
                  </p>
                )}
                <Label className="text-xs font-bold text-[#344054]">Correction Note</Label>
                <Textarea value={flagNote} onChange={(e) => setFlagNote(e.target.value)} placeholder="e.g. Posted with the wrong direction, needs reversal in SAP" className="text-xs" data-testid="cost-center-flag-note-input" />
                {detailEntry.needs_correction ? (
                  <Button size="sm" variant="outline" disabled={flagging} onClick={() => handleFlagToggle(false)} data-testid="cost-center-flag-clear-btn">
                    {flagging ? <CircleNotch size={12} className="animate-spin" /> : "Clear Flag"}
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled={flagging} onClick={() => handleFlagToggle(true)} className="text-[#B42318] border-[#FECDCA]" data-testid="cost-center-flag-set-btn">
                    {flagging ? <CircleNotch size={12} className="animate-spin" /> : "Flag as Needs Correction"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
